import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { User, UserRole, LoginHistoryLog, PwaIcon } from '../types';
// Fix: Import updateUser2FA to be used for enabling 2FA
import { getUsers, addUser as addUserToDb, deleteUser as deleteUserFromDb, updateUserPassword as updateUserPasswordInDb, updateUser2FA } from '../services/userService';
import { database } from '../services/firebase';
import { ref, onValue, get, set, push, serverTimestamp, remove, onDisconnect, update } from 'firebase/database';

export interface AppAssets {
  logo1?: string;
  logo2?: string;
  animatedLogo?: string;
  notificationSound?: string;
  crewIcon?: string;
  pilotIcon?: string;
  passengersIcon?: string;
}

export interface PushSubscriptionResult {
  success: boolean;
  reason?: 'denied' | 'missing_key' | 'unsupported' | 'generic';
}


// Fix: Added missing 2FA and password change methods to the context type.
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; twoFactorRequired?: boolean; userId?: string }>;
  register: (username: string, password: string, email: string, authCode: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  users: User[];
  activeUsers: User[];
  isRegistrationOpen: boolean;
  toggleRegistration: () => void;
  authCodes: { [code: string]: UserRole };
  updateAuthCodes: (newCodes: { [code: string]: UserRole }) => void;
  deleteUser: (userId: string) => Promise<void>;
  createUser: (userData: Omit<User, 'id' | 'authCode'>) => Promise<{ success: boolean; message: string }>;
  securityScanPercentage: number;
  updateSecurityScanPercentage: (percentage: number) => Promise<void>;
  updateUserPassword: (userId: string, password: string) => Promise<void>;
  appAssets: AppAssets;
  defaultAppAssets: AppAssets;
  updateAsset: (assetName: keyof AppAssets, dataUrl: string | null) => Promise<void>;
  isMultiFlightEnabled: boolean;
  toggleMultiFlight: () => Promise<void>;
  pwaIcons: PwaIcon[];
  updatePwaIcons: (icons: PwaIcon[]) => Promise<void>;
  vapidPublicKey: string | null;
  updateVapidPublicKey: (key: string) => Promise<void>;
  subscribeToPushNotifications: () => Promise<PushSubscriptionResult>;
  unsubscribeFromPushNotifications: () => Promise<boolean>;
  isSubscribedToPush: boolean;
  verifyTwoFactorCode: (userId: string, code: string) => Promise<{ success: boolean; message: string }>;
  changeUserPassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  generateTwoFactorSecret: () => Promise<{ secret: string; qrCodeUrl: string } | null>;
  enableTwoFactor: (secret: string, code: string) => Promise<{ success: boolean; message: string; recoveryCodes?: string[] }>;
  disableTwoFactor: (password: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultAssets: AppAssets = {
  crewIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTIgMTJhMyAzIDAgMTAwLTYgMyAzIDAgMDAwIDZ6IiAvPjxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTEyIDE1Yy00LjE0IDAtNy41IDIuMDE1LTcuNSA0LjVWMjFoMTV2LTEuNWMwLTIuNDg1LTMuMzYtNC41LTcuNS00LjV6IiAvPjwvc3ZnPg==',
  pilotIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTIgMTJhMyAzIDAgMTAwLTYgMyAzIDAgMDAwIDZ6IiAvPjxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTEyIDE1Yy00LjE0IDAtNy41IDIuMDE1LTcuNSA0LjVWMjFoMTV2LTEuNWMwLTIuNDg1LTMuMzYtNC41LTcuNS00LjV6IiAvPjxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTQuNSAxMi43NWgxNSIgLz48cGF0aCBzdHJva2UtbGluZWNhcD0icm9hdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNNiAxMi43NUw0LjUgOS43NWgzTDYgMTIuNzV6bTEyIDBsMS41LTNoLTNsMS41IDN6IiAvPjwvc3ZnPg==',
  passengersIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTUgMTkuMTI4YTkuMzggOS4zOCAwIDAwMi42MjUuMzczIDkuMzM3IDkuMzM3IDAgMDA0LjEyMS0uOTUyIDQuMTI1IDQuMTI1IDAgMDAtNy41MzMtMi40OTNtLTQuMDEtMS4wMzJhNi4zNzUgNi4zNzUgMCAwMTExLjk2NC00LjY4NC0xLjUgMS41IDAgMDAuMzI1LTEuNTVBOS4zNDggOS4zNDggMCAwMDE1IDQuNWEzLjM3NSAzLjM3NSAwIDAwLTYuNzUgMCA5LjM0NyA5LjM0NyAwIDAwLTEuMDg4IDQuMzg1Yy4wMy41MzUuMTg4IDEuMDUuNDIgMS41MjJhNi4zNzUgNi4zNzUgMCAwMTUuNjgtNC42ODR6IiAvPjxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTkgMTIuNzVhMy4zNzUgMy4zNzUgMCAxMS02Ljc1IDAgMy4zNzUgMy4zNzUgMCAwMTYuNzUgMHpNNC41IDE5LjEyOGEzLjM3NSAzLjM3NSAwIDAxLTMuMTI4LTQuNDcyIDMuMzc1IDMuMzc1IDAgMDE1LjMzMy0yLjAxMiIgLz48L3N2Zz4=',
};

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

// Fix: Added helper functions for TOTP generation and verification using Web Crypto API.
const base32tohex = (base32: string): string => {
    const base32chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
    let bits = "";
    let hex = "";
    for (let i = 0; i < base32.length; i++) {
        const val = base32chars.indexOf(base32.charAt(i).toUpperCase());
        if (val === -1) continue;
        bits += val.toString(2).padStart(5, '0');
    }
    for (let i = 0; i + 4 <= bits.length; i += 4) {
        const chunk = bits.substr(i, 4);
        hex += parseInt(chunk, 2).toString(16);
    }
    return hex;
};

const hexToArrayBuffer = (hex: string): ArrayBuffer => {
    const bytes = new Uint8Array(Math.ceil(hex.length / 2));
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes.buffer;
}

const verifyTOTP = async (secret: string, code: string): Promise<boolean> => {
    try {
        const keyHex = base32tohex(secret);
        const key = await crypto.subtle.importKey(
            'raw',
            hexToArrayBuffer(keyHex),
            { name: 'HMAC', hash: 'SHA-1' },
            false,
            ['sign']
        );
        const timeStep = Math.floor(Date.now() / 30000);
        // Check current, previous and next time steps to account for clock skew
        for (let i = -1; i <= 1; i++) {
            const currentStep = timeStep + i;
            const data = new ArrayBuffer(8);
            const view = new DataView(data);
            view.setUint32(4, currentStep, false);

            const signature = await crypto.subtle.sign('HMAC', key, data);
            const offset = new Uint8Array(signature)[19] & 0xf;
            const truncatedHash = new DataView(signature).getInt32(offset, false) & 0x7fffffff;
            const generatedCode = (truncatedHash % 1000000).toString().padStart(6, '0');

            if (generatedCode === code) {
                return true;
            }
        }
        return false;
    } catch (e) {
        console.error("TOTP verification error:", e);
        return false;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const savedUser = sessionStorage.getItem('currentUser');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [users, setUsers] = useState<User[]>([]);
    const [activeUsers, setActiveUsers] = useState<User[]>([]);
    const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
    const [authCodes, setAuthCodes] = useState<{ [code: string]: UserRole }>({});
    const [securityScanPercentage, setSecurityScanPercentage] = useState(10);
    const [appAssets, setAppAssets] = useState<AppAssets>({});
    const [isMultiFlightEnabled, setIsMultiFlightEnabled] = useState(false);
    const [pwaIcons, setPwaIcons] = useState<PwaIcon[]>([]);
    const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
    const [isSubscribedToPush, setIsSubscribedToPush] = useState(false);

    useEffect(() => {
        const usersRef = ref(database, 'users');
        const unsubscribe = onValue(usersRef, (snapshot) => {
            const usersVal = snapshot.val();
            if (usersVal) {
                setUsers(Object.values(usersVal));
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const configRef = ref(database, 'config');
        const unsubscribe = onValue(configRef, (snapshot) => {
            const config = snapshot.val();
            if (config) {
                setIsRegistrationOpen(config.registrationOpen || false);
                setAuthCodes(config.authCodes || {});
                setSecurityScanPercentage(config.securityScanPercentage || 10);
                setAppAssets(config.assets || {});
                setIsMultiFlightEnabled(config.isMultiFlightEnabled || false);
                setPwaIcons(config.pwaIcons || []);
                setVapidPublicKey(config.vapidPublicKey || null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!user) {
            setActiveUsers([]);
            return;
        }

        const statusRef = ref(database, 'status');
        const userStatusRef = ref(database, `status/${user.id}`);
        const userConnectionsRef = ref(database, `users/${user.id}/connections`);
        const connectedRef = ref(database, '.info/connected');

        const unsubscribeConnected = onValue(connectedRef, (snap) => {
            if (snap.val() === true) {
                const con = push(userConnectionsRef, true);
                onDisconnect(con).remove();
                
                set(userStatusRef, { username: user.username, role: user.role });
                onDisconnect(userStatusRef).remove();
            }
        });

        const unsubscribeStatus = onValue(statusRef, (snap) => {
            const active = snap.val();
            if (active) {
                setActiveUsers(Object.values(active));
            } else {
                setActiveUsers([]);
            }
        });

        return () => {
            unsubscribeConnected();
            unsubscribeStatus();
            remove(userStatusRef);
        };
    }, [user]);

    const logHistory = useCallback(async (action: 'login' | 'logout', loggedUser: User) => {
        const logEntry: Omit<LoginHistoryLog, 'timestamp'> = {
            userId: loggedUser.id,
            username: loggedUser.username,
            action,
        };
        if (action === 'login') {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                logEntry.ip = data.ip;
                logEntry.city = data.city;
                logEntry.region = data.region;
                logEntry.countryName = data.country_name;
            } catch (error) {
                console.warn('Could not fetch IP location:', error);
            }
        }
        await push(ref(database, 'loginHistory'), {
            ...logEntry,
            timestamp: serverTimestamp(),
        });
    }, []);

    // Fix: Updated login function to handle 2FA flow.
    const login = useCallback(async (username, password): Promise<{ success: boolean; message: string; twoFactorRequired?: boolean; userId?: string; }> => {
        const allUsers = await getUsers();
        const foundUser = allUsers.find(u => u.username.toLowerCase() === username.toLowerCase());
        if (!foundUser) {
            return { success: false, message: 'User not found.' };
        }
        if (foundUser.password !== password) {
            return { success: false, message: 'Incorrect password.' };
        }
        
        if (foundUser.twoFactorEnabled) {
            return { success: true, message: '2FA required.', twoFactorRequired: true, userId: foundUser.id };
        }
        
        setUser(foundUser);
        sessionStorage.setItem('currentUser', JSON.stringify(foundUser));
        await logHistory('login', foundUser);
        return { success: true, message: 'Logged in successfully.' };
    }, [logHistory]);

    const logout = useCallback(async () => {
        if (user) {
            try {
                await logHistory('logout', user);
                await remove(ref(database, `status/${user.id}`));
                await remove(ref(database, `users/${user.id}/connections`));
            } catch (error) {
                console.error("Error during logout cleanup:", error);
            }
        }
        setUser(null);
        sessionStorage.removeItem('currentUser');
    }, [user, logHistory]);

    const register = useCallback(async (username, password, email, authCode) => {
        try {
            const allUsers = await getUsers();
            if (allUsers.some(u => u.username.toLowerCase() === username.toLowerCase())) {
                return { success: false, message: 'Username already exists.' };
            }
            const snapshot = await get(ref(database, 'config/authCodes'));
            const codes = snapshot.val() || {};
            const role = codes[authCode];
            if (!role) {
                return { success: false, message: 'Invalid authorization code.' };
            }
            const newUser: User = {
                id: `user_${Date.now()}`,
                username,
                password,
                email,
                role,
                authCode,
            };
            await addUserToDb(newUser);
            return { success: true, message: 'Registration successful! You can now log in.' };
        } catch (error) {
            console.error("Registration error:", error);
            return { success: false, message: 'An unexpected error occurred during registration.' };
        }
    }, []);

    const toggleRegistration = useCallback(async () => {
        await set(ref(database, 'config/registrationOpen'), !isRegistrationOpen);
    }, [isRegistrationOpen]);
    
    const updateAuthCodes = useCallback(async (newCodes) => {
        await set(ref(database, 'config/authCodes'), newCodes);
    }, []);
    
    const deleteUser = useCallback(async (userId: string) => {
        await deleteUserFromDb(userId);
    }, []);

    const createUser = useCallback(async (userData) => {
        try {
            const allUsers = await getUsers();
            if (allUsers.some(u => u.username.toLowerCase() === userData.username.toLowerCase())) {
                return { success: false, message: 'Username already exists.' };
            }
            const newUser: User = {
                id: `user_${Date.now()}`,
                authCode: 'MANUAL_ADMIN_CREATE',
                ...userData,
            };
            await addUserToDb(newUser);
            return { success: true, message: 'User created successfully.' };
        } catch(error) {
            console.error("Create user error:", error);
            return { success: false, message: 'An unexpected error occurred creating the user.'};
        }
    }, []);

    const updateSecurityScanPercentage = useCallback(async (percentage: number) => {
        await set(ref(database, 'config/securityScanPercentage'), percentage);
    }, []);
    
    const updateUserPassword = useCallback(async (userId, password) => {
        await updateUserPasswordInDb(userId, password);
    }, []);
    
    const updateAsset = useCallback(async (assetName: keyof AppAssets, dataUrl: string | null) => {
        await set(ref(database, `config/assets/${assetName}`), dataUrl);
    }, []);

    const toggleMultiFlight = useCallback(async () => {
        await set(ref(database, 'config/isMultiFlightEnabled'), !isMultiFlightEnabled);
    }, [isMultiFlightEnabled]);

    const updatePwaIcons = useCallback(async (icons: PwaIcon[]) => {
        await set(ref(database, 'config/pwaIcons'), icons);
    }, []);
    
    const updateVapidPublicKey = useCallback(async (key: string) => {
        await set(ref(database, 'config/vapidPublicKey'), key);
    }, []);
    
    // Fix: Added implementations for all 2FA and password management functions.
    const changeUserPassword = useCallback(async (currentPassword, newPassword) => {
        if (!user) {
            return { success: false, message: 'Not logged in.' };
        }
        if (user.password !== currentPassword) {
            return { success: false, message: 'Incorrect current password.' };
        }
        try {
            await updateUserPasswordInDb(user.id, newPassword);
            const updatedUser = { ...user, password: newPassword };
            setUser(updatedUser);
            sessionStorage.setItem('currentUser', JSON.stringify(updatedUser));
            return { success: true, message: 'Password updated.' };
        } catch (e) {
            return { success: false, message: 'Failed to update password.' };
        }
    }, [user]);

    const generateTwoFactorSecret = useCallback(async () => {
        if (!user) return null;
        const base32chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
        let secret = "";
        const randomValues = new Uint8Array(32);
        crypto.getRandomValues(randomValues);
        for (let i = 0; i < randomValues.length; i++) {
            secret += base32chars[randomValues[i] & 31];
        }

        const otpAuthUrl = `otpauth://totp/JT-Crew:${user.username}?secret=${secret}&issuer=JT-Crew`;
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthUrl)}`;
        return { secret, qrCodeUrl };
    }, [user]);
    
    const verifyTwoFactorCode = useCallback(async (userId, code) => {
        const allUsers = await getUsers();
        const foundUser = allUsers.find(u => u.id === userId);

        if (!foundUser || !foundUser.twoFactorSecret) {
            return { success: false, message: 'User not found or 2FA not enabled.' };
        }

        const isValid = await verifyTOTP(foundUser.twoFactorSecret, code);

        if (isValid) {
            setUser(foundUser);
            sessionStorage.setItem('currentUser', JSON.stringify(foundUser));
            await logHistory('login', foundUser);
            return { success: true, message: 'Logged in successfully.' };
        } else {
            const recoveryCodes = foundUser.twoFactorRecoveryCodes || [];
            if (recoveryCodes.includes(code)) {
                const newRecoveryCodes = recoveryCodes.filter(rc => rc !== code);
                await updateUser2FA(foundUser.id, { twoFactorEnabled: true, twoFactorRecoveryCodes: newRecoveryCodes });
                const updatedUser = { ...foundUser, twoFactorRecoveryCodes: newRecoveryCodes };
                setUser(updatedUser);
                sessionStorage.setItem('currentUser', JSON.stringify(updatedUser));
                await logHistory('login', foundUser);
                return { success: true, message: 'Logged in successfully using recovery code.' };
            }
            return { success: false, message: 'Invalid 2FA code or recovery code.' };
        }
    }, [logHistory]);
    
    const enableTwoFactor = useCallback(async (secret, code) => {
        if (!user) return { success: false, message: 'Not logged in' };
        
        const isValid = await verifyTOTP(secret, code);
        if (!isValid) {
            return { success: false, message: 'Invalid verification code.' };
        }

        const recoveryCodes = Array.from({ length: 10 }, () => 
            Math.random().toString(36).substring(2, 8).toUpperCase()
        );

        try {
            await updateUser2FA(user.id, {
                twoFactorEnabled: true,
                twoFactorSecret: secret,
                twoFactorRecoveryCodes: recoveryCodes
            });
            const updatedUser = { ...user, twoFactorEnabled: true, twoFactorSecret: secret, twoFactorRecoveryCodes: recoveryCodes };
            setUser(updatedUser);
            sessionStorage.setItem('currentUser', JSON.stringify(updatedUser));
            return { success: true, message: '2FA enabled.', recoveryCodes };
        } catch (e) {
            return { success: false, message: 'Failed to save 2FA settings.' };
        }
    }, [user]);
    
    const disableTwoFactor = useCallback(async (password) => {
        if (!user) return { success: false, message: 'Not logged in.' };
        if (user.password !== password) {
            return { success: false, message: 'Incorrect password.' };
        }
        try {
            await update(ref(database, `users/${user.id}`), {
                twoFactorEnabled: false,
                twoFactorSecret: null,
                twoFactorRecoveryCodes: null
            });
            const updatedUser = { ...user, twoFactorEnabled: false };
            delete updatedUser.twoFactorSecret;
            delete updatedUser.twoFactorRecoveryCodes;

            setUser(updatedUser);
            sessionStorage.setItem('currentUser', JSON.stringify(updatedUser));
            return { success: true, message: '2FA disabled.' };
        } catch (e) {
            return { success: false, message: 'Failed to disable 2FA.' };
        }
    }, [user]);

    const subscribeToPushNotifications = useCallback(async (): Promise<PushSubscriptionResult> => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.error('Push messaging is not supported');
            return { success: false, reason: 'unsupported' };
        }
        if (!user) {
            return { success: false, reason: 'generic' }; // Should not happen if UI is correct
        }
        if (!vapidPublicKey) {
            console.error("VAPID key not configured by admin.");
            return { success: false, reason: 'missing_key' };
        }
        
        const registration = await navigator.serviceWorker.ready;
        try {
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
            await push(ref(database, `users/${user.id}/pushSubscriptions`), subscription.toJSON());
            setIsSubscribedToPush(true);
            return { success: true };
        } catch (error) {
            console.error('Failed to subscribe to push notifications', error);
            if (Notification.permission === 'denied') {
                return { success: false, reason: 'denied' };
            }
            return { success: false, reason: 'generic' };
        }
    }, [user, vapidPublicKey]);
    
    const unsubscribeFromPushNotifications = useCallback(async (): Promise<boolean> => {
        if (!user) return false;
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                const success = await subscription.unsubscribe();
                if (!success) {
                    console.error("Unsubscribe call failed.");
                    return false;
                }

                const subsSnapshot = await get(ref(database, `users/${user.id}/pushSubscriptions`));
                if (subsSnapshot.exists()) {
                    const subs = subsSnapshot.val();
                    for (const key in subs) {
                        if (subs[key].endpoint === subscription.endpoint) {
                            await remove(ref(database, `users/${user.id}/pushSubscriptions/${key}`));
                        }
                    }
                }
                setIsSubscribedToPush(false);
            }
            return true;
        } catch (error) {
            console.error("Failed to unsubscribe:", error);
            return false;
        }
    }, [user]);

    useEffect(() => {
        const checkSubscription = async () => {
            if ('serviceWorker' in navigator && user) {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    const subscription = await registration.pushManager.getSubscription();
                    setIsSubscribedToPush(!!subscription);
                } catch (error) {
                    console.error("Error checking push subscription status:", error);
                }
            }
        };
        checkSubscription();
    }, [user]);

    const value = useMemo(() => ({
        user, login, register, logout, users, activeUsers, isRegistrationOpen, toggleRegistration,
        authCodes, updateAuthCodes, deleteUser, createUser, securityScanPercentage,
        updateSecurityScanPercentage, updateUserPassword, appAssets, defaultAppAssets: defaultAssets,
        updateAsset, isMultiFlightEnabled, toggleMultiFlight, pwaIcons, updatePwaIcons,
        vapidPublicKey, updateVapidPublicKey, subscribeToPushNotifications, unsubscribeFromPushNotifications,
        isSubscribedToPush,
        // Fix: Expose new functions through the context provider.
        verifyTwoFactorCode, changeUserPassword, generateTwoFactorSecret, enableTwoFactor, disableTwoFactor
    }), [
        user, login, register, logout, users, activeUsers, isRegistrationOpen, toggleRegistration,
        authCodes, updateAuthCodes, deleteUser, createUser, securityScanPercentage,
        updateSecurityScanPercentage, updateUserPassword, appAssets, isMultiFlightEnabled,
        toggleMultiFlight, pwaIcons, updatePwaIcons, vapidPublicKey, updateVapidPublicKey,
        subscribeToPushNotifications, unsubscribeFromPushNotifications, isSubscribedToPush,
        // Fix: Add new functions to the dependency array.
        verifyTwoFactorCode, changeUserPassword, generateTwoFactorSecret, enableTwoFactor, disableTwoFactor
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};